import React, { useEffect, useState } from "react";
import { Document, BLOCKS } from "@contentful/rich-text-types";
import { NavLink } from "react-router-dom";

const locale = "en-US";

interface BlogPostContent {
  id: string;
  title: string;
  date: string;
  content: Document;
  hashtags: string[];
  image: string | null;
  status: "published" | "unpublished" | "archived";
}

interface ManifestEntry {
  id: string;
  title: string;
  date: string;
  hashtags: string[];
  image: string | null;
  status: "published" | "unpublished" | "archived";
}

const LatestBlogPost = () => {
  const [post, setPost] = useState<BlogPostContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const bucket = process.env.REACT_APP_FIREBASE_BUCKET;
  const contentPath = process.env.REACT_APP_CONTENT_PATH;

  useEffect(() => {
    if (!bucket || !contentPath) {
      setError("Firebase environment variables are not set.");
      setLoading(false);
      return;
    }

    const manifestUrl = `https://storage.googleapis.com/${bucket}/${encodeURIComponent(
      contentPath + "/manifest.json"
    )}`;

    const postJsonUrl = (id: string) =>
      `https://storage.googleapis.com/${bucket}/${encodeURIComponent(
        contentPath + "/" + id + ".json"
      )}`;

    async function fetchPost() {
      try {
        setLoading(true);
        setError(null);

        // Fetch manifest first
        const manifestResp = await fetch(manifestUrl);
        if (!manifestResp.ok) throw new Error("Failed to load manifest");
        const manifest: ManifestEntry[] = await manifestResp.json();

        const latest = manifest
          .filter((entry) => entry.status === "published")
          .reduce((prev, next) =>
            new Date(prev.date) > new Date(next.date) ? prev : next
          );

        if (!latest) {
          setPost(null);
          setError("Post not found or not published.");
          return;
        }

        // Fetch post JSON
        const postResp = await fetch(postJsonUrl(latest.id!));
        if (!postResp.ok) {
          throw new Error(`Failed to load post: ${postResp.status}`);
        }
        const data = await postResp.json();

        setPost({
          id: latest.id,
          title: data.fields?.title?.[locale] ?? "Untitled",
          content: data.fields?.content?.[locale] ?? {
            nodeType: "document",
            content: [],
          },
          hashtags: Array.isArray(data.fields?.hashtags?.[locale])
            ? data.fields.hashtags[locale]
            : [],
          date: latest.date,
          image: data.firebaseHeaderImage ?? latest.image ?? null, // fallback to manifest image
          status: "published",
        });
      } catch (err: any) {
        setPost(null);
        setError(err.message || "Error loading post");
      } finally {
        setLoading(false);
      }
    }

    fetchPost();
  }, [bucket, contentPath]);

  if (loading)
    return <div className="alert alert-info text-center">Loading...</div>;
  if (error)
    return <div className="alert alert-danger text-center">Error: {error}</div>;
  if (!post)
    return <div className="alert alert-warning text-center">Not Found</div>;

  const renderOptions = {
    renderNode: {
      [BLOCKS.QUOTE]: (_node: any, children: React.ReactNode) => (
        <blockquote className="blockquote border-start ps-3 my-4">
          {children}
        </blockquote>
      ),
    },
  };

  return (
    <div className={`page-section with-header-image`}>
      {post.image && (
        <div className="row mb-4 text-center g-0 justify-content-center">
          <img
            src={post.image}
            alt={post.title}
            className="img-fluid rounded shadow"
          />
        </div>
      )}
      <div className="container">
        <div className="row">
          {post.title && (
            <div className="col col-* text-start mb-3">
              <h1 className="fw-bold text-white">{post.title}</h1>
              <h4>{new Date(post.date).toLocaleDateString()}</h4>
            </div>
          )}
          <div className="col-auto">
            <NavLink to={`/post/${post.id}`}>
              <button className="primary">Read More!</button>
            </NavLink>
          </div>
        </div>

        {post.hashtags.length > 0 && (
          <div className="row">
            <section className="mt-4 col-auto">
              <div className="d-flex flex-wrap gap-2">
                {post.hashtags.map((tag, index) => (
                  <div
                    className="hashtag-link"
                    key={index}
                    onClick={() =>
                      window.open(
                        `https://www.facebook.com/hashtag/${tag}?locale=en_GB`,
                        "_blank"
                      )
                    }
                  >
                    <span className="hashtag-small">#{tag}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default LatestBlogPost;
